import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import {
  Building2,
  Plus,
  Users,
  CreditCard,
  GitBranch,
  DollarSign,
  AlertCircle,
  CheckCircle,
  PauseCircle,
  Copy
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function OrganizationsPage({ user, onLogout }) {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showAddCreditsDialog, setShowAddCreditsDialog] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [editingOrg, setEditingOrg] = useState(null);
  const [newOrg, setNewOrg] = useState({
    name: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    pricing_per_scan: 0.05,
    billing_cycle: 'monthly',
    prepaid_credits: 0
  });
  const [creditsToAdd, setCreditsToAdd] = useState({ credits: 0, payment_reference: '', notes: '' });

  const fetchOrganizations = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/organizations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setOrganizations(data);
      }
    } catch (error) {
      toast.error('Failed to fetch organizations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  const handleCreateOrg = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/organizations`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newOrg)
      });
      
      if (response.ok) {
        const created = await response.json();
        // Show a longer toast with the org ID + copy button
        toast.success(`Organization created: ${created.name}`, {
          description: `ID: ${created.id}`,
          duration: 8000,
          action: {
            label: 'Copy ID',
            onClick: () => {
              navigator.clipboard.writeText(created.id);
              toast.success('Organization ID copied');
            }
          }
        });
        setShowCreateDialog(false);
        setNewOrg({
          name: '',
          contact_email: '',
          contact_phone: '',
          address: '',
          pricing_per_scan: 0.05,
          billing_cycle: 'monthly',
          prepaid_credits: 0
        });
        fetchOrganizations();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create organization');
      }
    } catch (error) {
      toast.error('Failed to create organization');
    }
  };

  const handleAddCredits = async () => {
    if (!selectedOrg) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/organizations/${selectedOrg.id}/credits`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(creditsToAdd)
      });
      
      if (response.ok) {
        toast.success(`Added ${creditsToAdd.credits} credits to ${selectedOrg.name}`);
        setShowAddCreditsDialog(false);
        setCreditsToAdd({ credits: 0, payment_reference: '', notes: '' });
        fetchOrganizations();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add credits');
      }
    } catch (error) {
      toast.error('Failed to add credits');
    }
  };

  const handleEditOrg = (org) => {
    setEditingOrg({
      id: org.id,
      name: org.name,
      contact_email: org.contact_email || '',
      contact_phone: org.contact_phone || '',
      address: org.address || '',
      pricing_per_scan: org.pricing_per_scan || 0.05,
      billing_cycle: org.billing_cycle || 'monthly',
      status: org.status || 'active'
    });
    setShowEditDialog(true);
  };

  const handleUpdateOrg = async () => {
    if (!editingOrg) return;
    
    try {
      const token = localStorage.getItem('token');
      
      // Build update payload, only including non-empty values
      const updatePayload = {
        name: editingOrg.name || null,
        contact_email: editingOrg.contact_email || null,
        contact_phone: editingOrg.contact_phone || null,
        address: editingOrg.address || null,
        pricing_per_scan: editingOrg.pricing_per_scan || null,
        billing_cycle: editingOrg.billing_cycle || null,
        status: editingOrg.status || null
      };
      
      const response = await fetch(`${API_URL}/api/organizations/${editingOrg.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updatePayload)
      });
      
      if (response.ok) {
        toast.success('Organization updated successfully');
        setShowEditDialog(false);
        setEditingOrg(null);
        fetchOrganizations();
      } else {
        const error = await response.json();
        console.error('Update error:', error);
        toast.error(error.detail?.[0]?.msg || error.detail || 'Failed to update organization');
      }
    } catch (error) {
      console.error('Update exception:', error);
      toast.error('Failed to update organization');
    }
  };

  const handleStatusChange = async (orgId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/organizations/${orgId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (response.ok) {
        toast.success(`Organization ${newStatus}`);
        fetchOrganizations();
      }
    } catch (error) {
      toast.error('Failed to update organization');
    }
  };

  const handleDeleteOrg = async (orgId, orgName) => {
    if (!window.confirm(`Are you sure you want to delete "${orgName}"? This cannot be undone.`)) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/organizations/${orgId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success(`Organization "${orgName}" deleted`);
        fetchOrganizations();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete organization');
      }
    } catch (error) {
      toast.error('Failed to delete organization');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      active: { icon: CheckCircle, className: 'bg-green-100 text-green-700', label: 'Active' },
      suspended: { icon: PauseCircle, className: 'bg-red-100 text-red-700', label: 'Suspended' },
      pending: { icon: AlertCircle, className: 'bg-yellow-100 text-yellow-700', label: 'Pending' }
    };
    const c = config[status] || config.pending;
    return (
      <Badge className={c.className}>
        <c.icon className="w-3 h-3 mr-1" />
        {c.label}
      </Badge>
    );
  };

  const totalOrgs = organizations.length;
  const activeOrgs = organizations.filter(o => o.status === 'active').length;
  const totalCredits = organizations.reduce((sum, o) => sum + (o.remaining_credits || 0), 0);
  const totalUsers = organizations.reduce((sum, o) => sum + (o.total_users || 0), 0);

  return (
    <Layout user={user} onLogout={onLogout} activePage="organizations">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Organizations</h1>
            <p className="text-slate-500 mt-1">Manage tenant organizations and their billing</p>
          </div>
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button data-testid="create-org-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Organization
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Create New Organization</DialogTitle>
                <DialogDescription>
                  Set up a new tenant organization with billing configuration.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Organization Name</Label>
                  <Input
                    id="name"
                    value={newOrg.name}
                    onChange={(e) => setNewOrg({ ...newOrg, name: e.target.value })}
                    placeholder="ABC Remittance Corp"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Contact Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={newOrg.contact_email}
                    onChange={(e) => setNewOrg({ ...newOrg, contact_email: e.target.value })}
                    placeholder="admin@company.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Contact Phone</Label>
                  <Input
                    id="phone"
                    value={newOrg.contact_phone}
                    onChange={(e) => setNewOrg({ ...newOrg, contact_phone: e.target.value })}
                    placeholder="+1-555-1234"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="pricing">Price per Scan ($)</Label>
                    <Input
                      id="pricing"
                      type="number"
                      step="0.01"
                      min="0.02"
                      max="0.10"
                      value={newOrg.pricing_per_scan}
                      onChange={(e) => setNewOrg({ ...newOrg, pricing_per_scan: parseFloat(e.target.value) })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="billing">Billing Cycle</Label>
                    <Select
                      value={newOrg.billing_cycle}
                      onValueChange={(value) => setNewOrg({ ...newOrg, billing_cycle: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="yearly">Yearly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="credits">Initial Prepaid Credits</Label>
                  <Input
                    id="credits"
                    type="number"
                    min="0"
                    value={newOrg.prepaid_credits}
                    onChange={(e) => setNewOrg({ ...newOrg, prepaid_credits: parseInt(e.target.value) || 0 })}
                    placeholder="0"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
                <Button onClick={handleCreateOrg}>Create Organization</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Organizations</p>
                  <p className="text-2xl font-bold">{totalOrgs}</p>
                </div>
                <Building2 className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Active</p>
                  <p className="text-2xl font-bold text-green-600">{activeOrgs}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Credits Available</p>
                  <p className="text-2xl font-bold">{totalCredits.toLocaleString()}</p>
                </div>
                <CreditCard className="w-8 h-8 text-indigo-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Users</p>
                  <p className="text-2xl font-bold">{totalUsers}</p>
                </div>
                <Users className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Organizations Table */}
        <Card>
          <CardHeader>
            <CardTitle>All Organizations</CardTitle>
            <CardDescription>Manage organization accounts, billing, and access</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-slate-500">Loading...</div>
            ) : organizations.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                No organizations yet. Create your first organization to get started.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Organization</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Users</TableHead>
                    <TableHead>Branches</TableHead>
                    <TableHead>Credits</TableHead>
                    <TableHead>Pricing</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {organizations.map((org) => (
                    <TableRow key={org.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{org.name}</p>
                          <p className="text-sm text-slate-500">{org.contact_email}</p>
                          <div className="mt-1 inline-flex items-center gap-1 group">
                            <code
                              className="text-[10px] font-mono text-indigo-700 bg-indigo-50 px-1.5 py-0.5 rounded border border-indigo-200"
                              title={`Full ID: ${org.id}`}
                              data-testid={`org-id-${org.id}`}
                            >
                              {org.id}
                            </code>
                            <button
                              type="button"
                              onClick={() => {
                                navigator.clipboard.writeText(org.id);
                                toast.success('Organization ID copied');
                              }}
                              className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 text-slate-400 hover:text-indigo-600"
                              title="Copy ID"
                              data-testid={`copy-org-id-${org.id}`}
                            >
                              <Copy className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(org.status)}</TableCell>
                      <TableCell>
                        <div className="flex items-center">
                          <Users className="w-4 h-4 mr-1 text-slate-400" />
                          {org.total_users}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center">
                          <GitBranch className="w-4 h-4 mr-1 text-slate-400" />
                          {org.total_branches}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{org.remaining_credits?.toLocaleString()}</p>
                          <p className="text-xs text-slate-500">of {org.prepaid_credits?.toLocaleString()}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center">
                          <DollarSign className="w-4 h-4 text-slate-400" />
                          {org.pricing_per_scan}/scan
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEditOrg(org)}
                          >
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedOrg(org);
                              setShowAddCreditsDialog(true);
                            }}
                          >
                            <CreditCard className="w-3 h-3 mr-1" />
                            Add Credits
                          </Button>
                          {org.status === 'active' ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => handleStatusChange(org.id, 'suspended')}
                            >
                              Suspend
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-green-600 hover:text-green-700"
                              onClick={() => handleStatusChange(org.id, 'active')}
                            >
                              Activate
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => handleDeleteOrg(org.id, org.name)}
                          >
                            Delete
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Add Credits Dialog */}
        <Dialog open={showAddCreditsDialog} onOpenChange={setShowAddCreditsDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Credits</DialogTitle>
              <DialogDescription>
                Add prepaid scan credits to {selectedOrg?.name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="add-credits">Number of Credits</Label>
                <Input
                  id="add-credits"
                  type="number"
                  min="1"
                  value={creditsToAdd.credits}
                  onChange={(e) => setCreditsToAdd({ ...creditsToAdd, credits: parseInt(e.target.value) || 0 })}
                  placeholder="1000"
                />
                {selectedOrg && creditsToAdd.credits > 0 && (
                  <p className="text-sm text-slate-500">
                    Cost: ${(creditsToAdd.credits * selectedOrg.pricing_per_scan).toFixed(2)}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="payment-ref">Payment Reference</Label>
                <Input
                  id="payment-ref"
                  value={creditsToAdd.payment_reference}
                  onChange={(e) => setCreditsToAdd({ ...creditsToAdd, payment_reference: e.target.value })}
                  placeholder="INV-2026-001"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Input
                  id="notes"
                  value={creditsToAdd.notes}
                  onChange={(e) => setCreditsToAdd({ ...creditsToAdd, notes: e.target.value })}
                  placeholder="Monthly subscription"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddCreditsDialog(false)}>Cancel</Button>
              <Button onClick={handleAddCredits} disabled={creditsToAdd.credits <= 0}>
                Add {creditsToAdd.credits.toLocaleString()} Credits
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Organization Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Edit Organization</DialogTitle>
              <DialogDescription>
                Update organization details
              </DialogDescription>
            </DialogHeader>
            {editingOrg && (
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-name">Organization Name</Label>
                  <Input
                    id="edit-name"
                    value={editingOrg.name}
                    onChange={(e) => setEditingOrg({ ...editingOrg, name: e.target.value })}
                    placeholder="Organization name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-email">Contact Email</Label>
                  <Input
                    id="edit-email"
                    type="email"
                    value={editingOrg.contact_email}
                    onChange={(e) => setEditingOrg({ ...editingOrg, contact_email: e.target.value })}
                    placeholder="admin@organization.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-phone">Contact Phone</Label>
                  <Input
                    id="edit-phone"
                    value={editingOrg.contact_phone}
                    onChange={(e) => setEditingOrg({ ...editingOrg, contact_phone: e.target.value })}
                    placeholder="+1234567890"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-address">Address</Label>
                  <Input
                    id="edit-address"
                    value={editingOrg.address}
                    onChange={(e) => setEditingOrg({ ...editingOrg, address: e.target.value })}
                    placeholder="123 Business St"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-pricing">Price per Scan ($)</Label>
                  <Input
                    id="edit-pricing"
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={editingOrg.pricing_per_scan}
                    onChange={(e) => setEditingOrg({ ...editingOrg, pricing_per_scan: parseFloat(e.target.value) || 0.05 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-billing">Billing Cycle</Label>
                  <Select
                    value={editingOrg.billing_cycle}
                    onValueChange={(value) => setEditingOrg({ ...editingOrg, billing_cycle: value })}
                  >
                    <SelectTrigger id="edit-billing">
                      <SelectValue placeholder="Select billing cycle" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="yearly">Yearly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-status">Status</Label>
                  <Select
                    value={editingOrg.status}
                    onValueChange={(value) => setEditingOrg({ ...editingOrg, status: value })}
                  >
                    <SelectTrigger id="edit-status">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="suspended">Suspended</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
              <Button onClick={handleUpdateOrg} disabled={!editingOrg?.name}>
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
